import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const navigateRef = useRef(navigate);
    
    // Update ref when navigate changes
    useEffect(() => {
        navigateRef.current = navigate;
    }, [navigate]);

    useEffect(() => {
        // Check if user is logged in on mount
        const { tokenManager } = require('../utils/tokenManager');
        
        // Check for token first (more secure)
        const token = tokenManager.getAccessToken();
        const userData = localStorage.getItem('user_sis');
        
        if (token && userData) {
            try {
                const parsedUser = JSON.parse(userData);
                const keys = Object.keys(parsedUser || {});
                
                // Check if we have valid user data (at least schoolID and schoolname)
                if (keys.length >= 2 && parsedUser.schoolID && parsedUser.schoolname) {
                    setUser(parsedUser);
                    // If we're on the login page, redirect to home
                    if (window.location.hash === '#/login') {
                        navigateRef.current('/', { replace: true });
                    }
                } else {
                    // Invalid or incomplete data
                    console.warn('Invalid user data in localStorage, clearing...');
                    tokenManager.clearTokens();
                    if (window.location.hash !== '#/login') {
                        navigateRef.current('/login', { replace: true });
                    }
                }
            } catch (error) {
                console.error('Error parsing user data:', error);
                tokenManager.clearTokens();
                if (window.location.hash !== '#/login') {
                    navigateRef.current('/login', { replace: true });
                }
            }
        } else if (!token && userData) {
            // No token but data exists - might be from old session, keep it temporarily
            try {
                const parsedUser = JSON.parse(userData);
                if (parsedUser.schoolID && parsedUser.schoolname) {
                    setUser(parsedUser);
                    if (window.location.hash === '#/login') {
                        navigateRef.current('/', { replace: true });
                    }
                }
            } catch (error) {
                console.error('Error parsing user data:', error);
            }
        } else {
            // If no token and no user data and not on login page, redirect to login
            if (window.location.hash !== '#/login') {
                navigateRef.current('/login', { replace: true });
            }
        }
        setLoading(false);
    }, []); // No dependencies needed

    const login = async (credentials) => {
        try {
            // Import API config dynamically to avoid circular dependencies
            const { API_CONFIG } = await import('../config/api');
            const { sanitizeInput } = await import('../utils/sanitize');
            const { tokenManager } = await import('../utils/tokenManager');
            
            // Sanitize credentials before sending, but preserve password field
            // Password should not be sanitized as it may contain special characters
            const sanitizedCredentials = {
                ...sanitizeInput(credentials),
                password: credentials.password // Keep password as-is to avoid breaking special characters
            };
            
            const response = await axios.post(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.LOGIN}`,
                sanitizedCredentials,
                { timeout: 180000 } // 180 seconds (3 minutes) timeout
            );
            
            if (response.status === 200) {
                const responseData = response.data;
                
                // Handle both new JWT token response and legacy response
                let accessToken, refreshToken, userData;
                
                if (responseData.accessToken && responseData.refreshToken) {
                    // New JWT token response
                    accessToken = responseData.accessToken;
                    refreshToken = responseData.refreshToken;
                    userData = responseData.user;
                    
                    // Store tokens
                    tokenManager.setTokens(accessToken, refreshToken);
                } else {
                    // Legacy response - no tokens
                    userData = responseData;
                }
                
                // Store minimal user data
                const minimalUserData = {
                    _id: userData._id,
                    schoolID: userData.schoolID,
                    schoolname: userData.schoolname,
                    email: userData.email,
                    country: userData.country,
                    currency: userData.currency,
                    locale: userData.locale,
                    status: userData.status,
                    smsID: userData.smsID,
                    positiondescription: userData.positiondescription
                };
                
                localStorage.setItem('user_sis', JSON.stringify(minimalUserData));
                setUser(minimalUserData);
                navigate('/', { replace: true });
                return { success: true };
            }
            return {
                success: false,
                error: 'Login failed'
            };
        } catch (error) {
            // Enhanced error handling with specific messages
            let errorMessage = 'Login failed. Please try again.';
            
            if (error.response) {
                // Server responded with error status
                const status = error.response.status;
                const responseData = error.response.data;
                
                switch (status) {
                    case 202:
                        errorMessage = 'Wrong password. Please check your password and try again.';
                        break;
                    case 203:
                    case 204:
                    case 208:
                        errorMessage = 'Your account is not active. Please contact support.';
                        break;
                    case 206:
                        errorMessage = 'Access denied. Your account does not have permission to login.';
                        break;
                    case 400:
                        errorMessage = 'Invalid request. Please provide both email/username and password.';
                        break;
                    case 401:
                        errorMessage = 'Authentication failed. Please check your credentials.';
                        break;
                    case 403:
                        errorMessage = 'Access forbidden. You do not have permission.';
                        break;
                    case 429:
                        errorMessage = 'Too many login attempts. Please wait a few minutes.';
                        break;
                    case 500:
                    case 502:
                    case 503:
                        errorMessage = 'Server error. Please try again later.';
                        break;
                    default:
                        errorMessage = responseData?.message || responseData?.error || `Login failed with status ${status}.`;
                }
            } else if (error.request) {
                // Request was made but no response received
                if (error.code === 'ECONNABORTED') {
                    errorMessage = 'Request timeout. The server took too long to respond.';
                } else if (error.message && error.message.includes('Network Error')) {
                    errorMessage = 'Network error. Please check your connection.';
                } else {
                    errorMessage = 'Server connection timeout. Please check your connection.';
                }
            } else {
                // Error setting up the request
                errorMessage = error.message || 'Unable to process login request.';
            }
            
            return {
                success: false,
                error: errorMessage
            };
        }
    };

    const logout = async () => {
        const { tokenManager } = require('../utils/tokenManager');
        const { API_CONFIG } = await import('../config/api');
        
        try {
            // Call logout endpoint to clear httpOnly cookies on server
            await axios.post(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.LOGOUT}`,
                {},
                { withCredentials: true }
            );
        } catch (error) {
            // Continue with client-side cleanup even if server logout fails
            console.warn('Server logout failed, clearing client-side data:', error);
        }
        
        // Clear tokens from localStorage (for backward compatibility)
        tokenManager.clearTokens();
        // Clear user state
        setUser(null);
        // Clear any other auth-related localStorage items
        localStorage.removeItem('user_sis');
        localStorage.removeItem('token');
        // Navigate to login
        navigate('/login', { replace: true });
    };

    const value = {
        user,
        loading,
        login,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 